import * as React from 'react';
import { IntelligentPhotoCaptureAttachmentIterationStatus, IntelligentPhotoCaptureEstimatorCaptureMode } from '../enums';
import { IntelligentAssignmentSummary } from '../constants';
import { attachmentIteration } from '../utilities';
export const usePhotoCaptureIterationWorker = ({ attachmentIterationInfo, attachmentBatchActivityInfo, companyCode, fileSizeLimit, imageMaxWidth, maxFileCount, questionSubType, isPolling, maxPhotoLimitForBatch, onUpdateIterationStatus, onUpdateCameraFileStatus, onTakePhotoUploadSuccess, onUpdateIterationBatchCount, onEstimatorUpload, handleUploadContextAttachment, handleRequestDamageDetection, processedCompletedIterationIdsRef }) => {
    React.useEffect(() => {
        if (!attachmentIterationInfo.length) {
            return;
        }
        // Only allow one IN_PROGRESS iteration at a time
        const inProgressIteration = attachmentIteration.getInProgressIteration(attachmentIterationInfo);
        if (inProgressIteration) {
            if (inProgressIteration.captureMode === IntelligentPhotoCaptureEstimatorCaptureMode.TAKE_PHOTOS) {
                if (!inProgressIteration.cameraFiles) {
                    return;
                }
                const cameraFiles = inProgressIteration.cameraFiles;
                // Find the first PENDING camera file that is eligible to upload (first file or previous file completed)
                const nextPendingCameraFileIndex = cameraFiles.findIndex((cameraFile, idx) => {
                    if (cameraFile.cameraStatus !== IntelligentPhotoCaptureAttachmentIterationStatus.PENDING) {
                        return false;
                    }
                    // First camera file, or previous camera file is completed
                    if (idx === 0) {
                        return true;
                    }
                    return cameraFiles[idx - 1].cameraStatus === IntelligentPhotoCaptureAttachmentIterationStatus.COMPLETED;
                });
                if (nextPendingCameraFileIndex !== -1) {
                    const nextIterationFile = inProgressIteration.iterationFiles[nextPendingCameraFileIndex];
                    const imageName = cameraFiles[nextPendingCameraFileIndex].imageName;
                    if (nextIterationFile && imageName) {
                        onUpdateCameraFileStatus(inProgressIteration.iterationId, imageName, IntelligentPhotoCaptureAttachmentIterationStatus.IN_PROGRESS);
                        const onCameraFileUploadSuccess = (responseFiles) => {
                            if (!responseFiles || !responseFiles.length) {
                                return;
                            }
                            onTakePhotoUploadSuccess(inProgressIteration.iterationId, imageName, responseFiles[0].id);
                        };
                        onEstimatorUpload([nextIterationFile], attachmentIteration.getAllowedFileTypes(inProgressIteration.captureMode), companyCode, fileSizeLimit, questionSubType, imageMaxWidth, maxFileCount, onCameraFileUploadSuccess);
                    }
                }
            }
            return;
        }
        // Find the first iteration that is pending and eligible to start
        const nextIterationIdx = attachmentIterationInfo.findIndex((iteration, idx) => {
            if (iteration.iterationStatus !== IntelligentPhotoCaptureAttachmentIterationStatus.PENDING) {
                return false;
            }
            // First iteration, or previous iteration is complete
            if (idx === 0) {
                return true;
            }
            return (attachmentIterationInfo[idx - 1].iterationStatus ===
                IntelligentPhotoCaptureAttachmentIterationStatus.COMPLETED &&
                attachmentIterationInfo[idx - 1].isRenderingCompleted);
        });
        if (nextIterationIdx === -1) {
            return;
        }
        const nextIterationInfo = attachmentIterationInfo[nextIterationIdx];
        onUpdateIterationStatus(nextIterationInfo.iterationId, IntelligentPhotoCaptureAttachmentIterationStatus.IN_PROGRESS);
        if (nextIterationInfo.captureMode !== IntelligentPhotoCaptureEstimatorCaptureMode.TAKE_PHOTOS) {
            onEstimatorUpload(nextIterationInfo.iterationFiles, attachmentIteration.getAllowedFileTypes(nextIterationInfo.captureMode), companyCode, fileSizeLimit, questionSubType, imageMaxWidth, maxFileCount);
        }
    }, [attachmentIterationInfo]);
    React.useEffect(() => {
        if (!attachmentIterationInfo.length) {
            return;
        }
        const validatedBatchLimit = maxPhotoLimitForBatch > 0 ? maxPhotoLimitForBatch : IntelligentAssignmentSummary.MAX_PHOTO_LIMIT_FOR_BATCH;
        // Check for iterations with updated uploadFailedCount and recalculate batchCount
        attachmentIterationInfo.forEach((iteration) => {
            if (iteration.uploadFailedCount && iteration.uploadFailedCount > 0) {
                const fileCount = iteration.iterationFiles.length - iteration.uploadFailedCount;
                const newBatchCount = fileCount % validatedBatchLimit === 0
                    ? fileCount / validatedBatchLimit
                    : Math.floor(fileCount / validatedBatchLimit) + 1;
                if (newBatchCount !== iteration.batchCount) {
                    onUpdateIterationBatchCount(iteration.iterationId, newBatchCount);
                }
            }
        });
    }, [attachmentIterationInfo, maxPhotoLimitForBatch, onUpdateIterationBatchCount]);
    React.useEffect(() => {
        if (!attachmentIterationInfo.length || !attachmentBatchActivityInfo.length) {
            return;
        }
        let hasQueuedDamageDetectionInCurrentPass = attachmentIterationInfo.some((iteration) => iteration.damageCallStatus === IntelligentPhotoCaptureAttachmentIterationStatus.IN_PROGRESS);
        // Check for completed iterations
        attachmentIterationInfo.forEach((iteration) => {
            // Skip iterations that have already been fully processed in a previous render cycle
            if (processedCompletedIterationIdsRef.current.has(iteration.iterationId)) {
                return;
            }
            // If not completed, check if all batches are done and batchCount matches batchIds.length
            const shouldFinalizeCompletedIterationFromBatch = iteration.captureMode === IntelligentPhotoCaptureEstimatorCaptureMode.TAKE_PHOTOS
                ? iteration.iterationStatus !== IntelligentPhotoCaptureAttachmentIterationStatus.COMPLETED &&
                    iteration.batchIds.length > 0 &&
                    iteration.batchCount === iteration.batchIds.length &&
                    iteration.uploadingStatus === IntelligentPhotoCaptureAttachmentIterationStatus.COMPLETED
                : iteration.iterationStatus !== IntelligentPhotoCaptureAttachmentIterationStatus.COMPLETED &&
                    iteration.batchIds.length > 0 &&
                    iteration.batchCount === iteration.batchIds.length;
            if (shouldFinalizeCompletedIterationFromBatch) {
                // Confirm every batch belonging to this iteration has finished image analysis
                const allBatchIdsMatch = iteration.batchIds.every((batchId) => {
                    return attachmentBatchActivityInfo.some((batchInfo) => {
                        return (batchInfo.batchId === batchId &&
                            batchInfo.batchStatus === IntelligentPhotoCaptureAttachmentIterationStatus.COMPLETED);
                    });
                });
                if (allBatchIdsMatch) {
                    onUpdateIterationStatus(iteration.iterationId, IntelligentPhotoCaptureAttachmentIterationStatus.COMPLETED);
                }
            }
            // If completed and not rendered, handle context upload (only once)
            const shouldHandleUploadContextAttachment = iteration.captureMode === IntelligentPhotoCaptureEstimatorCaptureMode.TAKE_PHOTOS
                ? iteration.iterationStatus === IntelligentPhotoCaptureAttachmentIterationStatus.COMPLETED &&
                    iteration.uploadingStatus === IntelligentPhotoCaptureAttachmentIterationStatus.COMPLETED &&
                    !iteration.isRenderingCompleted
                : iteration.iterationStatus === IntelligentPhotoCaptureAttachmentIterationStatus.COMPLETED &&
                    !iteration.isRenderingCompleted;
            if (shouldHandleUploadContextAttachment) {
                handleUploadContextAttachment(iteration);
            }
            // If completed, rendered, and damage detection not yet called, call damage detection (only once)
            if (iteration.captureMode !== IntelligentPhotoCaptureEstimatorCaptureMode.UPLOAD_FILES &&
                iteration.iterationStatus === IntelligentPhotoCaptureAttachmentIterationStatus.COMPLETED &&
                iteration.isRenderingCompleted &&
                iteration.damageCallStatus === IntelligentPhotoCaptureAttachmentIterationStatus.PENDING) {
                if (isPolling) {
                    if (hasQueuedDamageDetectionInCurrentPass) {
                        // Do NOT add to processedCompletedIterationIdsRef so this iteration
                        // can be retried on the next render cycle.
                        return;
                    }
                    hasQueuedDamageDetectionInCurrentPass = true;
                }
                // Mark as processed before dispatching to prevent duplicate calls on re-render
                processedCompletedIterationIdsRef.current.add(iteration.iterationId);
                handleRequestDamageDetection(iteration.iterationId);
            }
        });
    }, [attachmentIterationInfo, attachmentBatchActivityInfo]);
};
//# sourceMappingURL=usePhotoCaptureIterationWorker.js.map